// perf.ts — the adaptive-offload decision (ARCHITECTURE.md §15.1), pure and
// clock-injected so it unit-tests without a browser. It answers one question:
// can this phone track live, or should the server take over the scan?
//
//  * Camera fps: the lower of the track's getSettings().frameRate and the
//    measured requestVideoFrameCallback/rAF rate (either may be missing),
//    defaulting to 30.
//  * Processing fps: WASM frameResults per second over a sliding 3 s window
//    that only starts after a 2 s warm-up (wasm init, first frames).
//  * Rule: once a full post-warm-up window exists, a windowed average below
//    cameraFps / 5 switches the session to server mode. A single long frame
//    (a GC pause) barely moves a 3 s average, so it cannot trip the switch.
//    The decision is one-way: after it fires, the monitor stays decided.
//  * Overrides: ?mode=server (or the "Process on server" toggle) forces
//    server mode from the start; ?mode=device disables the switch.
//  * Tracking holds (phone rotated, upload backlog) post no frames on
//    purpose, so suspend() restarts the window instead of counting them.

export type ProcessingMode = 'device' | 'server';
export type ModeOverride = 'auto' | 'device' | 'server';

export const DEFAULT_CAMERA_FPS = 30;
export const WARMUP_MS = 2000;
export const WINDOW_MS = 3000;
export const SLOW_FACTOR = 5;

// OffloadDecision is what gets logged, shown and sent with video/start.
export interface OffloadDecision {
  mode: ProcessingMode;
  camera_fps: number;
  processing_fps: number;
  reason: string;
  at_ms: number; // performance.now() of the decision
}

const validFps = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

// round1 keeps one decimal for display and the wire.
export const round1 = (v: number): number => Math.round(v * 10) / 10;

// resolveCameraFps is the lower of the advertised and the measured camera
// rate; with neither available it is DEFAULT_CAMERA_FPS.
export function resolveCameraFps(settingsFps?: number | null, measuredFps?: number | null): number {
  const c = [settingsFps, measuredFps].filter(validFps);
  return c.length ? Math.min(...c) : DEFAULT_CAMERA_FPS;
}

// parseModeOverride reads ?mode=server|device (anything else: auto).
export function parseModeOverride(search: string): ModeOverride {
  const m = new URLSearchParams(search).get('mode');
  return m === 'server' || m === 'device' ? m : 'auto';
}

// parseSimulateSlowMs reads ?simulateSlowMs=N (debug/e2e: delay every worker
// frame result by N ms). Invalid or negative values are 0.
export function parseSimulateSlowMs(search: string): number {
  const v = Number(new URLSearchParams(search).get('simulateSlowMs') ?? '0');
  return Number.isFinite(v) && v > 0 ? Math.min(v, 60000) : 0;
}

// slowReason words the decision for the UI and the log.
export function slowReason(processingFps: number, cameraFps: number): string {
  return `Phone too slow for live tracking (${round1(processingFps)} of ${round1(cameraFps)} fps) — processing on server`;
}

export interface PerfMonitorOptions {
  override?: ModeOverride;
  forced?: boolean; // the "Process on server" toggle
  warmupMs?: number;
  windowMs?: number;
  slowFactor?: number;
}

// PerfMonitor tracks frame-result throughput and decides when to offload.
export class PerfMonitor {
  readonly override: ModeOverride;
  private readonly warmupMs: number;
  private readonly windowMs: number;
  private readonly slowFactor: number;
  private settingsFps: number | null = null;
  private measuredFps: number | null = null;
  private windowFrom = Number.NaN; // results before this time are not counted
  private results: number[] = []; // result timestamps ≥ windowFrom, ascending
  private decided: OffloadDecision | null = null;

  constructor(opts: PerfMonitorOptions = {}) {
    this.override = opts.forced ? 'server' : (opts.override ?? 'auto');
    this.warmupMs = opts.warmupMs ?? WARMUP_MS;
    this.windowMs = opts.windowMs ?? WINDOW_MS;
    this.slowFactor = opts.slowFactor ?? SLOW_FACTOR;
  }

  get decision(): OffloadDecision | null {
    return this.decided;
  }

  get cameraFps(): number {
    return resolveCameraFps(this.settingsFps, this.measuredFps);
  }

  // threshold is the processing rate below which the phone is "too slow".
  get threshold(): number {
    return this.cameraFps / this.slowFactor;
  }

  setCameraFps(settingsFps: number | null | undefined, measuredFps: number | null | undefined): void {
    this.settingsFps = validFps(settingsFps) ? settingsFps : null;
    this.measuredFps = validFps(measuredFps) ? measuredFps : null;
    console.debug(`[openscan/perf] camera fps: settings=${this.settingsFps ?? '-'} measured=${this.measuredFps ?? '-'} → ${round1(this.cameraFps)} (switch below ${round1(this.threshold)})`);
  }

  // start begins the warm-up at tMs. A forced override decides immediately.
  start(tMs: number): OffloadDecision | null {
    this.windowFrom = tMs + this.warmupMs;
    this.results = [];
    console.debug(`[openscan/perf] monitor started (override=${this.override}), window opens at ${Math.round(this.windowFrom)} ms`);
    if (this.override === 'server' && !this.decided) {
      return this.decide(tMs, 0, 'Server processing requested — processing on server');
    }
    return null;
  }

  // suspend restarts the window at tMs: frames withheld on purpose (rotate
  // hint, upload backlog) say nothing about the phone's speed.
  suspend(tMs: number): void {
    if (this.decided || Number.isNaN(this.windowFrom)) return;
    if (this.results.length || this.windowFrom < tMs) console.debug(`[openscan/perf] window suspended at ${Math.round(tMs)} ms`);
    this.windowFrom = Math.max(this.windowFrom, tMs);
    this.results = [];
  }

  // processingFps is the result rate over the window ending at tMs (0 before
  // any full window is available).
  processingFps(tMs: number): number {
    this.prune(tMs);
    const span = Math.min(this.windowMs, tMs - this.windowFrom);
    return span > 0 ? (this.results.length * 1000) / span : 0;
  }

  // recordResult notes one completed frameResult and evaluates the rule.
  recordResult(tMs: number): OffloadDecision | null {
    if (!Number.isNaN(this.windowFrom) && tMs >= this.windowFrom) this.results.push(tMs);
    return this.check(tMs);
  }

  // check evaluates the rule at tMs; call it every animation frame too, so a
  // worker that stops answering altogether still trips the switch.
  check(tMs: number): OffloadDecision | null {
    if (this.decided || this.override !== 'auto' || Number.isNaN(this.windowFrom)) return null;
    if (tMs < this.windowFrom + this.windowMs) return null; // no full window yet
    const fps = this.processingFps(tMs);
    if (fps >= this.threshold) return null;
    return this.decide(tMs, fps, slowReason(fps, this.cameraFps));
  }

  private prune(tMs: number): void {
    const from = tMs - this.windowMs;
    let i = 0;
    while (i < this.results.length && this.results[i] < from) i++;
    if (i) this.results.splice(0, i);
  }

  private decide(tMs: number, fps: number, reason: string): OffloadDecision {
    this.decided = { mode: 'server', camera_fps: round1(this.cameraFps), processing_fps: round1(fps), reason, at_ms: tMs };
    console.info(`[openscan/perf] switching to server mode: ${reason} (camera ${round1(this.cameraFps)} fps, threshold ${round1(this.threshold)} fps)`);
    return this.decided;
  }
}
